import React, { useMemo } from 'react';
import { useAvContext } from '@context/AvContextProvider';
import {
  AddOperandButton,
  cleanValueFromExpression,
  ConditionType,
  countExpressions,
  Filter,
  filterOperatorOptions,
  getEmptyExpression,
  getEmptyFilterExpression,
  getRootOperator,
  getUniqueFieldNames,
  isIngressExp,
  OperatorType,
  RelationsProps,
  shouldRenderWithFieldFilterBox,
  StringConditionType,
} from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { emptyObject, isDeepEqual } from '@fabric-ui/utils';
import { Box } from '@mui/material';
import { FieldFilterBoxProps } from './FieldFilterBox';
import RenderOperator, { hasEmptyValueOperand } from './RenderOperator';

type CommonExpressionBuilderProps = {
  filter?: Filter;
  fields: any[];
  isLoadingFields?: boolean;
  isVertical?: boolean;
  size?: 'xSmall' | 'small';
  defaultField?: string | null;
  canAddNewFields?: boolean;
  canSelectFieldType?: boolean;
  supportParser?: boolean;
  autocompleteFilter?: Date[];
  canDeleteLast?: boolean;
  operatorOptions?: typeof filterOperatorOptions;
  ingressModeProps?: FieldFilterBoxProps['ingressModeProps'];
  relationsProps?: RelationsProps;
  fieldOnly?: boolean;
  hasListTab?: boolean;
  fieldSelectionLimit?: { limit: number; message: string };
  rowsLimit?: { limit: number; message: string };
  disabled?: boolean;
  fieldsListAdornment?: React.ReactNode;
  activeProjName?: string;
  icon?: React.ReactNode;
  isHistorical?: boolean;
  showAdvanced?: boolean;
  shouldShowIcon?: (operand: any) => boolean;
  defaultMode?: 'list' | 'condition';
  excludeNullFromList?: boolean;
};

type ExpressionBuilderProps = CommonExpressionBuilderProps &
  (
    | {
        readOnly: true;
        setFilter?: never;
      }
    | {
        readOnly?: false;
        setFilter: any;
      }
  );

const NewExpressionBuilder: React.FC<ExpressionBuilderProps> = ({
  setFilter,
  fields,
  isLoadingFields = false,
  isVertical = true,
  size = 'small',
  defaultField = null,
  canAddNewFields = false,
  canSelectFieldType,
  supportParser = false,
  autocompleteFilter,
  filter = getEmptyFilterExpression(null, supportParser ? StringConditionType.EQUALS : undefined),
  canDeleteLast = true,
  operatorOptions,
  ingressModeProps,
  relationsProps = emptyObject as RelationsProps,
  fieldOnly = false,
  fieldSelectionLimit,
  rowsLimit,
  disabled = false,
  readOnly = false,
  hasListTab = false,
  fieldsListAdornment,
  activeProjName,
  icon,
  isHistorical,
  showAdvanced,
  shouldShowIcon,
  defaultMode,
  excludeNullFromList,
}) => {
  const {
    accountEntities: { fieldMap },
  } = useAvContext();
  const rootOperator = getRootOperator<OperatorType>(filter);
  const oppositeOperator = rootOperator === OperatorType.OR ? OperatorType.AND : OperatorType.OR;

  const onAddGlobalOperand = (operator: OperatorType.OR | OperatorType.AND) => {
    const { operands } = filter[rootOperator]!;
    const lastOperand = operands.at(-1);
    const newItem =
      lastOperand.expression || lastOperand.and || lastOperand.or || isIngressExp(lastOperand)
        ? lastOperand
        : relationsProps.defaultEmptyFilter ||
          getEmptyExpression(
            defaultField,
            ConditionType.string,
            supportParser ? StringConditionType.EQUALS : undefined,
            fieldMap[defaultField!]
          );
    return setFilter({ [operator]: { operands: [...operands, cleanValueFromExpression(newItem, fieldMap)] } });
  };
  const selectedFieldNames = fieldSelectionLimit ? getUniqueFieldNames(filter) : new Set();
  const isOverFieldNamesLimit = fieldSelectionLimit && getUniqueFieldNames(filter).size >= fieldSelectionLimit.limit;
  const isOverExpressionsLimit = rowsLimit && countExpressions(filter) >= rowsLimit.limit;
  const disableMessage = isOverExpressionsLimit ? rowsLimit.message : undefined;
  const fieldSelectionMessage = isOverFieldNamesLimit ? fieldSelectionLimit.message : undefined;
  const modifiedFields = useMemo(
    () =>
      isOverFieldNamesLimit ? fields.map(field => (selectedFieldNames.has(field.value) ? field : { ...field, disabled: true })) : fields,
    [isOverFieldNamesLimit, fields, JSON.stringify([...selectedFieldNames])]
  );
  const disableAddOperandButton =
    isDeepEqual(getEmptyFilterExpression(null, supportParser ? StringConditionType.EQUALS : undefined), filter) ||
    isOverExpressionsLimit ||
    disabled;

  // TODO: Wrap in context to avoid prop drilling
  const commonProps = {
    size,
    isVertical,
    fields: modifiedFields,
    isLoadingFields,
    defaultField,
    canAddNewFields,
    canSelectFieldType,
    supportParser,
    canDeleteLast,
    operatorOptions,
    relationsProps,
    disableMessage,
    isOverExpressionsLimit,
    fieldSelectionMessage,
    fieldOnly,
    disabled,
    hasListTab,
    activeProjName,
    icon,
    isHistorical,
    showAdvanced,
    shouldShowIcon,
    defaultMode,
    excludeNullFromList,
  };

  const onToggleLogicalOperators = readOnly
    ? () => {}
    : () => {
        const toggleLogicalOperators = (globalFilter: Filter): Filter => {
          if (shouldRenderWithFieldFilterBox(globalFilter, hasListTab) && !(hasListTab && hasEmptyValueOperand(globalFilter))) {
            return globalFilter;
          }
          if (globalFilter.and || globalFilter.or) {
            return {
              [globalFilter.and ? OperatorType.OR : OperatorType.AND]: {
                operands: globalFilter[globalFilter.and ? OperatorType.AND : OperatorType.OR]!.operands.map(subFilter =>
                  isIngressExp(subFilter) ? subFilter : toggleLogicalOperators(subFilter)
                ),
              },
            };
          }
          return globalFilter;
        };
        setFilter(toggleLogicalOperators(filter));
      };

  return (
    <Box sx={{ ...flex.col }}>
      <RenderOperator
        {...commonProps}
        autocompleteFilter={autocompleteFilter}
        rootOperator={rootOperator}
        filter={filter}
        setFilter={setFilter}
        onToggleLogicalOperators={onToggleLogicalOperators}
        ingressModeProps={ingressModeProps}
        readOnly={readOnly}
        fieldsListAdornment={fieldsListAdornment}
      />
      {rowsLimit?.limit !== 1 && !readOnly && (
        <Box sx={{ ...flex.row, gap: 1, my: 1 }}>
          <AddOperandButton
            size={size}
            operator={rootOperator}
            isAdd
            onClick={() => onAddGlobalOperand(rootOperator)}
            disabled={disableAddOperandButton}
            disableMessage={disableMessage}
          />
          {filter[rootOperator].operands.length === 1 && (
            <AddOperandButton
              size={size}
              operator={oppositeOperator}
              isAdd
              onClick={() => onAddGlobalOperand(oppositeOperator)}
              disabled={disableAddOperandButton}
              disableMessage={disableMessage}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default NewExpressionBuilder;
